import api from '../../utils/api';

export type HealthStatus = 'healthy' | 'warning' | 'critical' | 'down' | 'unknown';

export interface OperationsDashboardResponse {
  success: boolean;
  data: OperationsDashboard;
}

export interface OperationsDashboard {
  generatedAt: string;
  refreshIntervalSeconds: number;
  meta?: {
    environment: 'local' | 'cloud_run';
    gcpAvailable: boolean;
    localMode: boolean;
    warnings: string[];
  };
  overall: {
    status: HealthStatus;
    healthScore: number;
    label: string;
  };
  sections: {
    backend: BackendHealth;
    database: DatabaseHealth;
    runtimeErrors: RuntimeErrorsSection;
    incidents: IncidentsSection;
    internalApis: InternalApisSection;
    externalServices: ExternalServicesSection;
    authentication: AuthSection;
    notifications: NotificationsSection;
    scheduler: SchedulerSection;
    queue: QueueSection;
    storage: StorageSection;
    security: SecuritySection;
    errorAnalytics: ErrorAnalyticsSection;
    apiAnalytics: ApiAnalyticsSection;
    deployments: DeploymentsSection;
    alerts: AlertsSection;
    uptime: UptimeSection;
    recommendations: Recommendation[];
  };
}

export interface BackendHealth {
  status: HealthStatus;
  backendStatus: HealthStatus;
  cloudRunStatus: string;
  cpuUsagePercent: number | null;
  memoryUsagePercent: number | null;
  runningInstances: number | null;
  minInstances: number | null;
  maxInstances: number | null;
  concurrency: number | null;
  requestsPerMinute: number | null;
  latencyMs: number | null;
  count4xx: number | null;
  count5xx: number | null;
  containerRestarts: number | null;
  startupFailures: number | null;
  latestRevision: string | null;
  currentVersion: string | null;
  currentCommitSha: string | null;
  deploymentTime: string | null;
  lastDeploymentStatus: string | null;
  uri: string | null;
  source?: string;
  gcpAvailable?: boolean;
  notice?: string | null;
}

export interface DatabaseHealth {
  status: HealthStatus;
  cloudSqlStatus: string;
  cpuUsagePercent: number | null;
  memoryUsagePercent: number | null;
  storageUsedPercent: number | null;
  connections: number | null;
  queryResponseTimeMs: number | null;
  queryResponseTimeFormatted: string | null;
  backups: Array<{ id: string; status: string; startTime: string }>;
  replication: string;
  pendingMigrations: string[];
  slowQueries: Array<{ query: string; executions: number; avgMs: number }>;
  deadlocks: number | null;
  dbReady: boolean;
}

export interface RuntimeErrorRow {
  id?: string;
  timestamp?: string;
  latestTimestamp?: string;
  severity: string;
  message: string;
  stackTrace?: string | null;
  revision?: string | null;
  commitSha?: string | null;
  category: string;
  occurrences?: number;
  appName?: string;
  platform?: string;
  serviceCategory?: string;
  pageRoute?: string | null;
  userId?: string | null;
  userRole?: string | null;
  statusCode?: string;
  errorType?: string;
  deviceInfo?: Record<string, unknown> | null;
  payloadSnapshot?: Record<string, unknown> | null;
  isOfflineReplay?: boolean;
  status?: 'UNRESOLVED' | 'INVESTIGATING' | 'RESOLVED' | string;
}

export interface RuntimeErrorsSection {
  status: HealthStatus;
  latest: RuntimeErrorRow[];
  grouped: RuntimeErrorRow[];
  from: string;
  to: string;
  source?: string;
  gcpLoggingAvailable?: boolean;
  notice?: string | null;
}

export interface IncidentsSection {
  status: HealthStatus;
  open: Array<Record<string, unknown>>;
  configured: Array<Record<string, unknown>>;
}

export interface InternalApiEndpoint {
  name: string;
  method: string;
  path: string;
  status: HealthStatus;
  responseTimeMs: number;
  httpStatus?: number;
  availability: number;
  successRate: number;
  errorPercent: number;
  lastFailure?: string | null;
  lastSuccess?: string | null;
  errorMessage?: string;
}

export interface InternalApisSection {
  status: HealthStatus;
  endpoints: InternalApiEndpoint[];
  averageResponseTimeMs: number;
  availabilityPercent: number;
}

export interface ExternalServiceHealth {
  id: string;
  name: string;
  status: HealthStatus;
  latencyMs: number | null;
  lastSuccess?: string | null;
  lastFailure?: string | null;
  errorMessage?: string | null;
  retryCount: number;
}

export interface ExternalServicesSection {
  status: HealthStatus;
  services: ExternalServiceHealth[];
}

export interface AuthSection {
  status: HealthStatus;
  checks: Array<{ name: string; status: HealthStatus; message: string }>;
}

export interface NotificationsSection {
  status: HealthStatus;
  channels: Record<string, { pending: number | null; sent: number | null; failed: number | null }>;
  summary: { pending: number; sent: number; failed: number };
}

export interface SchedulerSection {
  status: HealthStatus;
  jobs: Array<Record<string, unknown>>;
}

export interface QueueSection {
  status: HealthStatus;
  queues: Array<{
    name: string;
    pending: number;
    running: number;
    failed: number;
    retry: number;
    status: HealthStatus;
  }>;
  available: boolean;
}

export interface StorageSection {
  status: HealthStatus;
  provider: string;
  bucket?: string;
  available: boolean;
  errors: number;
  latencyMs?: number;
  errorMessage?: string;
}

export interface SecuritySection {
  status: HealthStatus;
  ssl: { enabled: boolean; provider: string };
  jwtExpiry: string;
  apiKeys: Array<{ name: string; configured: boolean }>;
  secretManager: { totalListed: number; expiredSecrets: Array<Record<string, unknown>> };
  credentialChecks: Array<{ name: string; status: HealthStatus; loaded: boolean }>;
}

export interface ErrorAnalyticsSection {
  todayTotal: number;
  byCategory: Record<string, number>;
  groupedByFrequency: Array<{
    message: string;
    category: string;
    occurrences: number;
    latestTimestamp: string;
    severity: string;
  }>;
}

export interface ApiAnalyticsSection {
  topApis: Array<Record<string, unknown>>;
  slowApis: Array<Record<string, unknown>>;
  mostFailedApis: Array<Record<string, unknown>>;
  highestLatencyApis: Array<Record<string, unknown>>;
}

export interface DeploymentsSection {
  history: Array<{
    revision: string;
    createdAt: string;
    commitSha: string | null;
    branch: string | null;
    version: string | null;
    status: string;
  }>;
  latest: DeploymentsSection['history'][0] | null;
}

export interface AlertsSection {
  configured: Array<Record<string, unknown>>;
  critical: Array<Record<string, unknown>>;
  warning: Array<Record<string, unknown>>;
}

export interface UptimeSection {
  last24Hours: { availabilityPercent: number | null; totalRequests: number };
  last7Days: { availabilityPercent: number | null; totalRequests: number };
  last30Days: { availabilityPercent: number | null; totalRequests: number };
}

export interface Recommendation {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  action: string;
}

export interface LiveLogsResponse {
  success: boolean;
  data: { rows: Array<Record<string, unknown>> };
}

export async function fetchOperationsDashboard(): Promise<OperationsDashboard> {
  const response = await api.get<OperationsDashboardResponse>('/master-admin/operations/dashboard', {
    timeout: 120000,
  });
  return response.data.data;
}

export async function refreshOperationsDashboard(): Promise<OperationsDashboard> {
  const response = await api.post<OperationsDashboardResponse>('/master-admin/operations/dashboard/refresh', undefined, {
    timeout: 120000,
  });
  return response.data.data;
}

export async function fetchOperationsErrors(params?: {
  range?: string;
  category?: string;
  search?: string;
  limit?: number;
  page?: number;
  offset?: number;
  platform?: string;
  appName?: string;
  serviceCategory?: string;
  statusCode?: string;
  status?: string;
  userSearch?: string;
  severity?: string;
}): Promise<{ rows: RuntimeErrorRow[]; grouped: RuntimeErrorRow[]; total?: number; hasMore?: boolean; page?: number }> {
  const response = await api.get('/master-admin/operations/errors', { params });
  return response.data.data;
}

export async function updateErrorStatus(id: string, status: string) {
  const response = await api.patch(`/telemetry/errors/${id}/status`, { status });
  return response.data;
}

export async function fetchLiveLogs(params?: { limit?: number; search?: string; severity?: string }) {
  const response = await api.get<{
    success: boolean;
    data: {
      rows: Array<Record<string, unknown>>;
      source?: string;
      gcpLoggingAvailable?: boolean;
      notice?: string | null;
    };
  }>('/master-admin/operations/logs/live', { params });
  return response.data.data;
}

export async function rollbackDeployment(revision: string) {
  const response = await api.post('/master-admin/operations/deployments/rollback', { revision });
  return response.data;
}

export function statusColor(status: HealthStatus | string): string {
  switch (status) {
    case 'healthy':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'warning':
      return 'text-amber-600 dark:text-amber-400';
    case 'critical':
    case 'down':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-500 dark:text-gray-400';
  }
}

export function statusBg(status: HealthStatus | string): string {
  switch (status) {
    case 'healthy':
      return 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800';
    case 'warning':
      return 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800';
    case 'critical':
    case 'down':
      return 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800';
    default:
      return 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700';
  }
}

export function statusEmoji(status: HealthStatus | string): string {
  switch (status) {
    case 'healthy':
      return '🟢';
    case 'warning':
      return '🟠';
    case 'critical':
    case 'down':
      return '🔴';
    default:
      return '⚪';
  }
}
